import { graphql } from 'gatsby';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout/Layout';
import HBMediaElement from '@honeybook-ui/HBMediaElement/HBMediaElement';
import NavigateLink from '../components/Link/NavigateLink';
import { trackError } from '../utils/error.utils';

import '../styles/404.scss';
import UtilsService from '../Services/UtilsService';

const ogTags = {
    url: 'https://www.honeybook.com/404',
    title: 'Page not found',
    description: 'Error 404: Page not found',
    noindex: true
};

type Props = {
    title?: string;
    subtitle?: string;
    buttonText?: string;
};
export const NotFound = ({ title, subtitle, buttonText }: Props) => {
    const { t } = useTranslation();

    useEffect(() => {
        trackError('404 error', {
            pathname: window.location.pathname,
            referrer: document.referrer
        });
    }, []);

    return (
        <Layout
            customClass="not-found"
            ogTags={ogTags}
            slimNav
            noFooter
            noPromo
        >
            <div className="not-found__container">
                <div className="not-found__content">
                    <HBMediaElement
                        customClass="not-found__sorry"
                        name="marketing_site/404/Sorry_Lockup__as_one.svg"
                        alt=""
                        lazyLoad={false}
                        forceImgTag
                    />
                    <HBMediaElement
                        customClass="not-found__sorry--mweb"
                        name="marketing_site/404/Sorry_Lockup.svg"
                        alt=""
                        forceImgTag
                        lazyLoad={false}
                    />
                    <HBMediaElement
                        customClass="not-found__monitor"
                        name="marketing_site/404/Error.gif"
                        alt=""
                        lazyLoad={false}
                    />
                    <p className="not-found__title">
                        {title || t('NotFound.title')}
                    </p>
                    <p className="not-found__subtitle">
                        {subtitle || t('NotFound.subtitle')}
                    </p>
                    <NavigateLink
                        source="404_to_home"
                        link={UtilsService.getOrigin()}
                        className="not-found__link hbui-button hbui-button--large hbui-button--primary--dark"
                    >
                        {buttonText || t('NotFound.buttonText')}
                    </NavigateLink>
                    <nav
                        className="not-found__recovery"
                        aria-labelledby="not-found-recovery-label"
                    >
                        <p
                            className="not-found__recovery-label"
                            id="not-found-recovery-label"
                        >
                            {t('NotFound.recoveryLabel')}
                        </p>
                        <ul className="not-found__recovery-list">
                            <li>
                                <NavigateLink
                                    source="404_to_recovery"
                                    link="/sitemap.xml"
                                    className="not-found__recovery-link"
                                >
                                    {t('NotFound.recoverySitemap')}
                                </NavigateLink>
                            </li>
                            <li>
                                <NavigateLink
                                    source="404_to_recovery"
                                    link="/llms.txt"
                                    className="not-found__recovery-link"
                                >
                                    {t('NotFound.recoveryLlms')}
                                </NavigateLink>
                            </li>
                            <li>
                                <NavigateLink
                                    source="404_to_recovery"
                                    link="https://developers.honeybook.com"
                                    className="not-found__recovery-link"
                                >
                                    {t('NotFound.recoveryDevelopers')}
                                </NavigateLink>
                            </li>
                            <li>
                                <NavigateLink
                                    source="404_to_recovery"
                                    link="https://help.honeybook.com"
                                    className="not-found__recovery-link"
                                >
                                    {t('NotFound.recoveryHelp')}
                                </NavigateLink>
                            </li>
                        </ul>
                    </nav>
                    <HBMediaElement
                        customClass="not-found__arrow"
                        name="marketing_site/404/Arrow_Desktop.svg"
                        alt=""
                        forceImgTag
                        lazyLoad={false}
                    />
                </div>
            </div>
        </Layout>
    );
};

const NotFoundPage = () => {
    return <NotFound />;
};

export default NotFoundPage;

export const query = graphql`
    query ($language: String!) {
        locales: allLocale(
            filter: {
                ns: { in: ["common", "404"] }
                language: { eq: $language }
            }
        ) {
            edges {
                node {
                    ns
                    data
                    language
                }
            }
        }
    }
`;
